import { Controller, useFormContext } from "react-hook-form"
import { t } from "i18next"
import { RadioGroup } from "@/components/ui/radio-group"
import {
  getSlotLabel,
  MORNING_LAST_SLOT_ID,
  NIGHT_LAST_SLOT_ID,
  NOON_LAST_SLOT_ID,
  SLOT_MAX,
  SLOT_MIN,
} from "@/lib/slot"
import { TimeSlotList } from "@/modules/patients/agenda/teleconsultation/booking/components/time-slot-list"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { useAvailableSlotsCapacityQuery$data } from "@/modules/patients/services/__generated__/useAvailableSlotsCapacityQuery.graphql"

export interface MappingSlot {
  slotId: number
  value: string
  available: boolean
  allowPriorityBooking: boolean
}

interface SlotDateProps {
  availableSlotsCapacity: useAvailableSlotsCapacityQuery$data | null
  isLoading: boolean
}

export const SlotDate = ({ availableSlotsCapacity: data, isLoading }: SlotDateProps) => {
  const { control, setValue } = useFormContext<TeleconsultationForm>()

  const slotsCapacity = data?.availableSlotsCapacity?.result

  if (!slotsCapacity) return null

  const splitAndMapSlots = (beginSlot: number, endSlot: number): MappingSlot[] => {
    const slots = slotsCapacity.filter((slot) => slot.slotId >= beginSlot && slot.slotId <= endSlot)

    return slots.map((slot) => ({
      slotId: slot.slotId,
      value: getSlotLabel(slot.slotId),
      available: slot.isAvailable,
      allowPriorityBooking: slot.allowPriorityBooking,
    }))
  }

  const nightSlots = splitAndMapSlots(SLOT_MIN, NIGHT_LAST_SLOT_ID)
  const morningSlots = splitAndMapSlots(NIGHT_LAST_SLOT_ID + 1, MORNING_LAST_SLOT_ID)
  const noonSlots = splitAndMapSlots(MORNING_LAST_SLOT_ID + 1, NOON_LAST_SLOT_ID)
  const eveningSlots = splitAndMapSlots(NOON_LAST_SLOT_ID + 1, SLOT_MAX)

  const renderSlotsGroup = (slots: MappingSlot[], label: string) =>
    slots.length > 0 && (
      <div className="grid gap-2">
        <label className="text-sm font-normal">{label}</label>
        <TimeSlotList slots={slots} />
      </div>
    )

  return (
    <Controller
      name="slotId"
      control={control}
      render={({ field: { value } }) => (
        <RadioGroup
          disabled={isLoading}
          value={value?.toString()}
          onValueChange={(value: string) => setValue("slotId", +value, { shouldDirty: true, shouldValidate: true })}>
          <div className="grid gap-4" data-testid="patient-teleconsultation-time-slot">
            {renderSlotsGroup(nightSlots, t("patient.agenda.teleconsultationForm.night"))}
            {renderSlotsGroup(morningSlots, t("patient.agenda.teleconsultationForm.morning"))}
            {renderSlotsGroup(noonSlots, t("patient.agenda.teleconsultationForm.noon"))}
            {renderSlotsGroup(eveningSlots, t("patient.agenda.teleconsultationForm.evening"))}
          </div>
        </RadioGroup>
      )}
    />
  )
}
